import {Injectable} from '@angular/core';
import {registerPlugin} from '@capacitor/core';
import {isNativeCapacitorPlatform} from '../shared/platform';

const REFRESH_TOKEN_KEY = 'refresh-token';

interface SecureStorageGetResult {
  value: string | null;
}

export interface AuthSecureStoragePlugin {
  get(options: {key: string}): Promise<SecureStorageGetResult>;
  remove(options: {key: string}): Promise<void>;
  set(options: {key: string; value: string}): Promise<void>;
}

export const AuthSecureStorage = registerPlugin<AuthSecureStoragePlugin>('AuthSecureStorage');

export async function readNativeRefreshToken(storage: AuthSecureStoragePlugin): Promise<string | null> {
  try {
    const result = await storage.get({key: REFRESH_TOKEN_KEY});
    return result.value?.trim() || null;
  } catch {
    await storage.remove({key: REFRESH_TOKEN_KEY}).catch(() => undefined);
    return null;
  }
}

@Injectable({providedIn: 'root'})
export class AuthRefreshTokenStorage {
  async read(): Promise<string | null> {
    if (!isNativeCapacitorPlatform()) {
      return null;
    }

    return readNativeRefreshToken(AuthSecureStorage);
  }

  async write(refreshToken: string): Promise<void> {
    if (!isNativeCapacitorPlatform()) {
      return;
    }

    const normalizedToken = refreshToken.trim();
    if (!normalizedToken) {
      throw new Error('A native refresh token is required.');
    }

    await AuthSecureStorage.set({key: REFRESH_TOKEN_KEY, value: normalizedToken});
  }

  async clear(): Promise<void> {
    if (!isNativeCapacitorPlatform()) {
      return;
    }

    await AuthSecureStorage.remove({key: REFRESH_TOKEN_KEY});
  }
}
